import {
  AbandonedCabin,
  AbandonedCartsRequest,
  AdultPassengerDetails,
  ChildPassengerDetails,
  IdentityDocument,
} from '@msc-b2c/interfaces/interfaces';
import { StepName } from '@msc-b2c/interfaces/enums';
import { GuestData } from '@msc-b2c/interfaces/types';
import {
  SendAbandonedCarts,
  RootState,
  store,
  getAbandonedCartCampaignCode,
  getEnableAbandonedCart,
} from '@msc-b2c/shared-utils';

// Steps considered "post guest-info" where abandoned-cart tracking is allowed.
const POST_GUEST_INFO_STEPS = new Set<string>([
  StepName.CRUISE_SUMMARY,
  StepName.ADD_ONS,
  StepName.INSURANCE,
  StepName.CHECKOUT,
]);

// Fallback ages used when payload data is incomplete.
const DEFAULT_PASSENGER_AGE = {
  adult: 18,
  child: 1,
} as const;

// Normalize prefix into API shape (+XX), preserving existing values when already normalized.
const normalizePhonePrefix = (prefix?: string): string | undefined => {
  if (!prefix?.trim()) {
    return undefined;
  }

  const cleanedPrefix = prefix.trim().replaceAll(/\s+/g, '');
  return cleanedPrefix.startsWith('+') ? cleanedPrefix : `+${cleanedPrefix}`;
};

const normalizePhoneNumber = (phone?: string): string | undefined => {
  if (!phone?.trim()) {
    return undefined;
  }

  return phone.replaceAll(/-/g, '').trim();
};

const normalizeGender = (gender?: string): AdultPassengerDetails['gender'] | undefined => {
  const normalizedGender = String(gender || '')
    .trim()
    .toUpperCase();

  if (normalizedGender === 'MALE' || normalizedGender === 'FEMALE') {
    return normalizedGender;
  }

  return undefined;
};

// Map optional identity document fields and enforce PASSPORT-specific attributes when available.
const mapIdentityDocument = (passenger: GuestData): IdentityDocument | undefined => {
  if (!passenger.documentType || !passenger.documentNumber || !passenger.dateOfExpire) {
    return undefined;
  }

  const identityDocument: IdentityDocument = {
    type: passenger.documentType.toUpperCase() as IdentityDocument['type'],
    number: passenger.documentNumber,
    expiryDate: passenger.dateOfExpire,
  };

  if (passenger.documentType.toUpperCase() === 'PASSPORT') {
    if (passenger.countryOfIssue) {
      identityDocument.issuedAt = passenger.countryOfIssue;
    }
    if (passenger.passportDateOfIssue) {
      identityDocument.issueDate = passenger.passportDateOfIssue;
    }
  }

  return identityDocument;
};

const isAdultPassenger = (passenger: GuestData): boolean =>
  passenger.type?.toLowerCase() === 'adult';

// Centralized typed state access for utility functions.
const getRootStateFromStore = (): RootState => store.getState() as unknown as RootState;

// Extract passenger numbers only, excluding undefined placeholders.
const getPassengerNumbers = (passengers: GuestData[] | undefined): number[] =>
  passengers
    ?.map((passenger) => passenger.number)
    .filter((number): number is number => number !== undefined) || [];

// Build a cabin payload only when at least one meaningful field exists.
const buildCabinPayload = (
  categoryCode?: string,
  passengers?: number[]
): AbandonedCabin | undefined => {
  if (!categoryCode && (!passengers || passengers.length === 0)) {
    return undefined;
  }

  return {
    ...(categoryCode && { categoryCode }),
    ...(passengers && passengers.length > 0 && { passengers }),
  };
};

// Shared passenger mapper used by adult/child builders.
const mapPassengerBase = (
  passenger: GuestData,
  index: number,
  defaultAge: number
): ChildPassengerDetails => {
  const email = passenger.email?.trim();
  const phone = normalizePhoneNumber(passenger.phone);
  const phonePrefix = normalizePhonePrefix(passenger.prefix);
  const gender = normalizeGender(passenger.gender);
  const identityDocument = mapIdentityDocument(passenger);

  return {
    number: passenger.number || index + 1,
    age: passenger.age || defaultAge,
    dateOfBirth: passenger.dateOfBirth || '',
    firstName: passenger.firstName || '',
    lastName: passenger.lastName || '',
    ...(gender && { gender }),
    ...(email && { email }),
    ...(phone && { phone }),
    ...(phonePrefix && { phonePrefix }),
    ...(passenger.countryOfResidence && {
      address: {
        country: passenger.countryOfResidence,
      },
    }),
    ...(passenger.nationality && {
      nationality: passenger.nationality,
    }),
    ...(identityDocument && { identityDocument }),
  };
};

// Adult payload includes leadPassenger flag required by abandoned-cart validation.
const mapAdultPassenger = (passenger: GuestData, index: number): AdultPassengerDetails => ({
  ...mapPassengerBase(passenger, index, DEFAULT_PASSENGER_AGE.adult),
  leadPassenger: index === 0,
});

// Child payload uses shared base mapping with child fallback age.
const mapChildPassenger = (passenger: GuestData, index: number): ChildPassengerDetails =>
  mapPassengerBase(passenger, index, DEFAULT_PASSENGER_AGE.child);

// Resolve passengerType by precedence from booking params to selected cabin metadata.
const getPassengerType = (state: RootState): string => {
  const { bookingParams, bookingStepsStore } = state;

  const passengerTypes = [
    bookingParams?.passengerType,
    bookingStepsStore?.firstCabinSelections?.selectedCategory?.passengerType,
    bookingStepsStore?.firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
      ?.passengerType,
    bookingStepsStore?.secondCabinSelections?.selectedCategory?.passengerType,
    bookingStepsStore?.secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
      ?.passengerType,
  ];

  return passengerTypes.find((value): value is string => Boolean(value)) || '';
};

const getCabinsPayload = (state: RootState): AbandonedCartsRequest['cabins'] => {
  const { firstCabinSelections, secondCabinSelections } = state.bookingStepsStore;

  const firstCabinCategoryCode =
    firstCabinSelections.selectedCategory?.categoryCode ||
    firstCabinSelections.selectedCabinPreferenceObj?.lowestPricedCategory?.categoryCode;

  const secondCabinCategoryCode =
    secondCabinSelections.selectedCategory?.categoryCode ||
    secondCabinSelections.selectedCabinPreferenceObj?.lowestPricedCategory?.categoryCode;

  const firstCabinPassengers = getPassengerNumbers(firstCabinSelections.passengersData);
  const secondCabinPassengers = getPassengerNumbers(secondCabinSelections.passengersData);

  const cabins = [
    buildCabinPayload(firstCabinCategoryCode, firstCabinPassengers),
    buildCabinPayload(secondCabinCategoryCode, secondCabinPassengers),
  ].filter((cabin): cabin is AbandonedCabin => Boolean(cabin));

  return cabins.length > 0 ? cabins : undefined;
};

export const isAbandonedCartStepEligible = (stepName?: string): boolean =>
  POST_GUEST_INFO_STEPS.has(stepName || '');

// Feature gate + step gate for deciding if abandoned-cart should run.
export const shouldTriggerAbandonedCart = (): boolean => {
  if (!getEnableAbandonedCart()) {
    return false;
  }

  const currentStepName = getRootStateFromStore()?.bookingStepsStore?.currentStep?.name;
  return isAbandonedCartStepEligible(currentStepName);
};

// Compose abandoned-cart request from current Redux booking state.
export const buildAbandonedCartRequest = (state: RootState): AbandonedCartsRequest | undefined => {
  const firstCabinPassengers = state.bookingStepsStore.firstCabinSelections.passengersData || [];
  const secondCabinPassengers = state.bookingStepsStore.secondCabinSelections.passengersData || [];

  const allPassengers = [...firstCabinPassengers, ...secondCabinPassengers];
  const adultsSource = allPassengers.filter(isAdultPassenger);
  const childrenSource = allPassengers.filter((passenger) => !isAdultPassenger(passenger));

  if (adultsSource.length === 0) {
    return undefined;
  }

  const adults = adultsSource.map((passenger, index) => mapAdultPassenger(passenger, index));
  const leadPassenger = adults.find((passenger) => passenger.leadPassenger);

  if (!leadPassenger?.email || !leadPassenger.phone || !leadPassenger.phonePrefix) {
    return undefined;
  }

  const children = childrenSource.map((passenger, index) => mapChildPassenger(passenger, index));
  const campaignCode = getAbandonedCartCampaignCode() || '';

  return {
    submissionDateTime: Math.floor(Date.now() / 1000),
    campaignCode,
    cruiseId: state.bookingParams.cruiseID || undefined,
    passengerInformation: {
      passengerType: getPassengerType(state),
      adults: {
        total: adults.length,
        details: adults,
      },
      ...(children.length > 0 && {
        children: {
          total: children.length,
          details: children,
        },
      }),
    },
    cabins: getCabinsPayload(state),
  };
};

// Evaluate eligibility to trigger abandoned cart call + build payload together.
export const getAbandonedCartRequestForCurrentState = (): AbandonedCartsRequest | undefined => {
  if (!shouldTriggerAbandonedCart()) {
    return undefined;
  }

  return buildAbandonedCartRequest(getRootStateFromStore());
};

export const sendAbandonedCartRequest = async (
  requestData: AbandonedCartsRequest
): Promise<void> => {
  try {
    await new SendAbandonedCarts(false, false, requestData, undefined).sendData();
  } catch (error) {
    console.error('Failed to send abandoned cart payload.', error);
  }
};
